/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, DestroyRef, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferMarkForCheck} from '../util/defer-mark-for-check';
import {createBufferedFlag, readBufferedFlag} from '../util/template-state';
import template from './top-articles-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_STAT_TYPES} from '../options/chart-options';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {AngularModalService} from '../modal/angular-modal.service';
import {AdnShotterService} from '../adn-shotter.service';
import {TopArticlesScreenshottingModalComponent} from './top-articles-screenshotting-modal.component';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {BrowserLocationService} from '../../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-top-articles-table',
  template
})
export class TopArticlesTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() children: any;
  @Input() parentType: string;
  @Input() dateSelectorId: string;

  sel: any = {};
  sorting = {
    orderByParam: 'impressions',
    asc: true
  };

  isLocalhost = false;
  parentParam: string;
  defaultTopN = 50;
  topN = 50;
  loaded = false;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  error = false;
  articles: any[] = null;
  otherRow: any = null;
  totals: any = null;
  totalCount: number;
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);
  dateOption: any;
  countType = ADN_CHART_COUNT_TYPES.singular;
  statType = ADN_CHART_STAT_TYPES.impression;
  useAlt: any;
  canceller: any;

  screenshots: any[] = null;
  screenshotArticles: string[] = [];
  screenshotting = false;
  deleting = false;
  hasQueued = false;
  showScreenshots = false;
  isSaving = false;

  targetingUrls: {[url: string]: string} = {};
  targetingUrlsOpen: string | false = false;
  openTargetingArticle: string | null = null;

  private chartDateListener: () => void;
  private chartOptionObject: any;
  private params: any = {};
  private configured = false;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  constructor(
    private readonly statsResource: StatsResource,
    private readonly ngZone: NgZone,
    private readonly angularModalService: AngularModalService,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly adnShotter: AdnShotterService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType || changes.dateSelectorId) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.isLocalhost = this.location.hostname === 'localhost';
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    if (!this.chartOptionObject) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.initParams();

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.parentParam = this.chartOptionObject.apiParam;

    this.defaultTopN = 50;
    this.topN = this.defaultTopN || 50;

    this.fetchShots();
    this.checkTargeting();

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      this.applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      this.applyChartDates(lastDates);
    }
  }

  onTopNChange(newN: number): void {
    this.topN = newN;
    this.articles = null;
    this.error = false;
    this.callForStats();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
  }

  changeCallback(dateOptionId?: any, startDate?: any, endDate?: any): void {
    if (dateOptionId && this.chartOptionObject) {
      this.dateOption = this.ChartOptionsUtil.getDateOptions(this.chartOptionObject.caps)[dateOptionId];
    }
    // Chart-options also broadcasts chartDates, but this callback updates the
    // bound dates first, so the listener would treat the range as unchanged
    // and skip the stats request. Apply the selected range here.
    this.applyChartDates({startDate, endDate});
  }

  saveCallback(): void {
  }

  refresh(): void {
    this.fetchShots();
  }

  deleteScreenshot(screenshot: any, index: number): void {
    this.deleting = true;
    this.adnShotter.deleteScreenshot(screenshot).then(() => {
      this.ngZone.run(() => {
        _.pullAt(this.screenshots, index);
      });
    }).finally(() => {
      this.ngZone.run(() => {
        this.deleting = false;
        if (this.screenshots.length === 0) {
          this.hasQueued = false;
        }
      });
    });
  }

  clearAllTargeting(): void {
    this.object.targeting.articleTarget.negated = false;
    this.object.targeting.articleTarget.urls = [];
    this.saveTargeting();
  }

  resetTargeting(): void {
    this.checkTargeting();
  }

  targetArticle(url: string): void {
    if (this.object.targeting.articleTarget.urls.length === 0) {
      this.object.targeting.articleTarget.negated = false;
    }
    this.object.targeting.articleTarget.urls.push(url);
    this.saveTargeting();
  }

  excludeArticle(url: string): void {
    if (this.object.targeting.articleTarget.urls.length === 0) {
      this.object.targeting.articleTarget.negated = true;
    }
    this.object.targeting.articleTarget.urls.push(url);
    this.saveTargeting();
  }

  removeFromTargeting(url: string): void {
    this.object.targeting.articleTarget.urls = _.filter(this.object.targeting.articleTarget.urls, (targetUrl) => {
      return targetUrl !== url;
    });
    this.saveTargeting();
  }

  toggleTargetingMenu(article: string): void {
    this.openTargetingArticle = this.openTargetingArticle === article ? null : article;
  }

  async screenshottingOptions(articleUrls?: any): Promise<void> {
    const articles = this.filterScreenshots(
      _.isArray(articleUrls) ? articleUrls : _.isString(articleUrls) ? [articleUrls] : this.screenshotArticles
    );
    const modalRef = await this.angularModalService.showAfterTick(TopArticlesScreenshottingModalComponent, {
      class: 'modal-lg',
      initialState: {
        object: this.object,
        children: this.children,
        articles,
        networkTag: this.LocalNetworkProfile.getNetworkTag()
      }
    }, this.destroyRef);
    if (!modalRef) {
      return;
    }
    const modalContent = modalRef.content as TopArticlesScreenshottingModalComponent;
    modalContent.onTriggered.subscribe(() => {
      this.ngZone.run(() => {
        this.hasQueued = true;
        this.fetchShots();
        this.showScreenshots = true;
      });
    });
  }

  triggerDefaultScreenshots(): void {
    const allPromises = [];
    _.forEach(this.screenshotArticles, (article) => {
      allPromises.push(this.adnShotter.takeShot(null, this.object.id, article, 'DESKTOP'));
    });
    this.hasQueued = true;
    Promise.all(allPromises).then(() => {
      this.fetchShots();
      this.ngZone.run(() => {
        this.showScreenshots = true;
      });
    });
  }

  sortUpdate(field: any): void {
    if (field.key === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
      return;
    }
    this.sorting.orderByParam = field.key;
    this.sorting.asc = true;
  }

  sortedArticles(): any[] {
    if (!this.articles) {
      return [];
    }
    const key = this.sorting.orderByParam;
    // The old template's orderBy expression treated this flag as reverse.
    const reverse = this.sorting.asc;
    return _.orderBy(this.articles, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [reverse ? 'desc' : 'asc']);
  }

  fieldValue(row: any, field: any): any {
    return row[this.useAlt ? (field.altId || field.id) : field.key];
  }

  private applyChartDates(data: {startDate?: any; endDate?: any}): void {
    if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
      return;
    }
    // Defer date bindings — lastDates / chartDates can land during parent CD
    // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
    deferMarkForCheck(this.cdr, () => {
      if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
        return;
      }
      this.initParams();

      this.startDate.set(data.startDate);
      this.endDate.set(data.endDate);

      this.articles = null;
      this.error = false;

      if (this.startDate() && this.endDate()) {
        this.params.startDate = this.startDate();
        this.params.endDate = this.endDate();
      }

      this.params.aggregateTo = 'article';
      this.params.groupBy = null;

      this.callForStats();
    });
  }

  private initParams(): void {
    this.params = {
      id: this.object.id,
      idKey: this.chartOptionObject.apiParam + 'Id'
    };
  }

  private fetchShots(): void {
    this.screenshotting = true;
    this.adnShotter.fetchScreenshots(this.object.id).then((data: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.screenshots = data;
        _.forEach(this.screenshots, (s) => {
          s.articleUrlHttps = (s.articleUrl || '').indexOf('http') === 0 ? s.articleUrl : 'https://' + s.articleUrl;
        });
        this.hasQueued = !!_.find(this.screenshots, (s) => {
          return s.registrationStatus !== 'FINALISED';
        });
      });
    }).finally(() => {
      this.ngZone.run(() => {
        this.screenshotting = false;
      });
    });
  }

  private checkTargeting(): void {
    this.targetingUrls = {};
    const safeUrls = _.get(this.object, ['targeting', 'articleTarget', 'urls'], []);
    _.forEach(safeUrls, (url) => {
      this.targetingUrls[url] = this.object.targeting.articleTarget.negated ? 'EXCLUDED' : 'INCLUDED';
    });
    this.targetingUrlsOpen = false;
    if (safeUrls.length > 0) {
      this.targetingUrlsOpen = this.object.targeting.articleTarget.negated ? 'EXCLUDED' : 'INCLUDED';
    }
  }

  private saveTargeting(): void {
    this.isSaving = true;
    this.object.savePartial(['targeting']).then((savedLineItem: any) => {
      this.ngZone.run(() => {
        this.checkTargeting();
        this.isSaving = false;
        this.eventBus.emit('lineItemTargetingUpdate', savedLineItem);
      });
    });
  }

  private filterScreenshots(articles: any[]): string[] {
    const selectedSites = [];
    return _.filter(_.map(articles, (art) => {
      if (!art || String(art).indexOf('all-others-stats-group-id') !== -1) {
        return null;
      }
      return (art || '').indexOf('http') === 0 ? art : 'https://' + art;
    }), (art) => {
      if (!art) {
        return false;
      }
      const url = new URL(art);
      const hostName = url.hostname;
      const isSelected = selectedSites.indexOf(hostName) < 0 && selectedSites.length < 3;
      if (isSelected) {
        selectedSites.push(hostName);
      }
      return isSelected;
    });
  }

  private callForStats(): void {
    this.params.topN = this.topN;
    this.loaded = false;
    this.statsResource.getArticles(this.params, (data: any) => {
      const articles = data.chunks;

      const allOthersIndex = _.findIndex(data.chunks, (row: any) => {
        return row.article === 'all-others-stats-group-id';
      });

      const totals = data.totals;
      const totalCount = data.totalCount;

      const otherRow: any = allOthersIndex > -1 ? _.pullAt(data.chunks, allOthersIndex)[0] : null;
      if (otherRow) {
        const remainingObjects = data.totalCount - this.params.topN;
        otherRow.updatedName = remainingObjects + ' remaining articles';
      }

      const screenshotArticles = this.filterScreenshots(_.map(articles, 'article'));

      articles.forEach((article) => {
        if (article.matchScore) {
          if (article.matchScore === 'SLIGHTLY_RELATED') {
            article.matchScore = 1;
          }
          if (article.matchScore === 'SOMEWHAT_RELATED') {
            article.matchScore = 2;
          }
          if (article.matchScore === 'MODERATELY_RELATED') {
            article.matchScore = 3;
          }
          if (article.matchScore === 'CLOSELY_RELATED') {
            article.matchScore = 4;
          }
          if (article.matchScore === 'VERY_CLOSELY_RELATED') {
            article.matchScore = 5;
          }
        }
      });

      deferMarkForCheck(this.cdr, () => {
        this.articles = articles;
        this.totals = totals;
        this.totalCount = totalCount;
        this.otherRow = otherRow;
        this.screenshotArticles = screenshotArticles;
        this.loaded = true;
      });
    }, (err: any) => {
      this.adnLog.warn(err);
      deferMarkForCheck(this.cdr, () => {
        this.error = true;
      });
    }, this);
  }
}
